import { Button } from '@heidi/design-system/button';
import { TypographyCaption } from '@heidi/design-system/typography';
import type { TranslationKey } from '@heidi/runtime/i18n';
import { useTranslations } from '@heidi/runtime/i18n';
import { FileUp, Plus } from 'lucide-react';
import { useRef } from 'react';
import { USER_DOCUMENTS_ACCEPT_FORMATS } from '../../../contracts';
import { getFileIcon } from '../DocumentMetadataPopover.utils';
import { createLibraryUploadFile, type LibraryUploadFile } from '../upload.types';
import { DocumentSearchInput, type SelectedExistingDoc } from './DocumentSearchInput';
import { InlineEditableName } from './InlineEditableName';
import { RemovableDocRow } from './RemovableDocRow';
import { TeamToggleContent, type TeamToggleStepProps } from './TeamCollectionSwitch';
import { WizardStepFooter } from './WizardStepFooter';

interface DocumentsStepProps extends TeamToggleStepProps {
  name: string;
  onNameChange: (name: string) => void;
  stagedFiles: LibraryUploadFile[];
  onAddFiles: (files: LibraryUploadFile[]) => void;
  onRemoveFile: (uid: string) => void;
  selectedExistingDocs: SelectedExistingDoc[];
  onAddExistingDoc: (doc: SelectedExistingDoc) => void;
  onRemoveExistingDoc: (id: string) => void;
  onBack?: () => void;
  onCancel: () => void;
  onSave: () => void;
  isSaving: boolean;
  error: string | null;
}

export { DocumentsStepContent } from './DocumentsStepContent';

/**
 * Step 2B of the Create Collection Wizard.
 * Shows a search input for library documents, a list of staged/selected documents,
 * and allows adding more via file upload.
 */
export const DocumentsStep = ({
  name,
  onNameChange,
  stagedFiles,
  onAddFiles,
  onRemoveFile,
  selectedExistingDocs,
  onAddExistingDoc,
  onRemoveExistingDoc,
  isTeamCollection,
  onTeamCollectionChange,
  hasTeam,
  canCreateTeamCollection,
  teamName,
  onBack,
  onCancel,
  onSave,
  isSaving,
  error,
}: DocumentsStepProps) => {
  const { t } = useTranslations();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const totalDocuments = stagedFiles.length + selectedExistingDocs.length;

  const handleAddClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const fileList = e.target.files;
    if (fileList && fileList.length > 0) {
      onAddFiles(Array.from(fileList).map(createLibraryUploadFile));
    }
    e.target.value = '';
  };

  const removeDocLabel = t('library.wizard.documents.removeLabel' as TranslationKey);
  const removeFileLabel = t('library.wizard.documents.removeFileLabel' as TranslationKey);

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-hidden">
      {/* Header with inline editable name */}
      <div className="flex items-center gap-2">
        <InlineEditableName value={name} onChange={onNameChange} className="flex-1" />
      </div>

      {/* Search + upload row */}
      <div className="flex shrink-0 items-center gap-2">
        <DocumentSearchInput
          excludedDocumentIds={selectedExistingDocs.map((d) => d.id)}
          onSelectDocument={onAddExistingDoc}
          placeholder={t('library.wizard.documents.searchPlaceholder' as TranslationKey)}
          className="min-w-0 flex-1"
        />
        <Button
          type="button"
          variant="secondary"
          size="md"
          className="shrink-0"
          onClick={handleAddClick}
        >
          <Plus />
          {t('library.wizard.documents.addButton' as TranslationKey)}
        </Button>
      </div>

      {/* Document list — grows to fill available space */}
      <div className="min-h-0 flex-1 overflow-y-auto">
        {totalDocuments === 0 ? (
          <div className="flex h-full flex-col items-center justify-center gap-4">
            <FileUp size={24} className="text-text-tertiary" />
            <TypographyCaption className="text-text-tertiary">
              {t('library.wizard.documents.empty' as TranslationKey)}
            </TypographyCaption>
          </div>
        ) : (
          <div className="overflow-y-auto">
            {selectedExistingDocs.map((doc) => (
              <RemovableDocRow
                key={doc.id}
                icon={getFileIcon(doc.filename)}
                label={doc.filename}
                onRemove={() => onRemoveExistingDoc(doc.id)}
                removeLabel={removeDocLabel}
              />
            ))}
            {stagedFiles.map((file) => (
              <RemovableDocRow
                key={file.uid}
                icon={getFileIcon(file.name)}
                label={file.name}
                onRemove={() => onRemoveFile(file.uid)}
                removeLabel={removeFileLabel}
              />
            ))}
          </div>
        )}
      </div>

      {/* Hidden file input for "Add document" */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept={USER_DOCUMENTS_ACCEPT_FORMATS}
        onChange={handleFileInputChange}
        className="hidden"
      />

      {/* Footer */}
      <WizardStepFooter
        onBack={onBack}
        onCancel={onCancel}
        onSave={onSave}
        isSaving={isSaving}
        isSaveDisabled={totalDocuments === 0}
        error={error}
        className="pt-2"
        leftContent={
          <TeamToggleContent
            isTeamCollection={isTeamCollection}
            onTeamCollectionChange={onTeamCollectionChange}
            hasTeam={hasTeam}
            canCreateTeamCollection={canCreateTeamCollection}
            teamName={teamName}
          />
        }
      />
    </div>
  );
};
